// Copia en el navegador (IndexedDB) de las consultas livianas que alimentan contadores y listas de entrada: al
// recargar, el menu, Hoy y la Bandeja muestran al instante lo ultimo que se vio y se actualizan por detras. Solo se
// guardan las consultas de la lista blanca (nada de correos completos, fichas ni reportes), una copia por empresa y
// persona, que vence a las 24 h o con cada version nueva de la Consola. No se usa en el puesto compartido (API key) y
// se borra al cerrar sesion. Si el navegador no tiene IndexedDB (modo privado, bloqueado) simplemente no hay copia.
import { defaultShouldDehydrateQuery, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { PersistedClient, PersistQueryClientOptions, Persister } from '@tanstack/react-query-persist-client';

import type { Session } from '../api/http';

export const EDAD_MAXIMA = 24 * 60 * 60_000;
// Cambia con cada compilacion (vite.config.ts): una version nueva no lee copias con la forma vieja de los datos.
export const VERSION_CACHE = String(import.meta.env.VITE_BUILD_ID ?? 'dev');

// Consultas que se guardan. null = cualquier valor en esa posicion de la clave; lo que sigue al patron tambien vale.
const LISTA_BLANCA: (string | null)[][] = [
  ['llamadas', '14d'],
  ['tareas'],
  ['casos-vista', 'sin-cerrar'],
  ['vistas-conteo'],
  ['vistas-caso'],
  ['estados-hilos'],
  ['miembros'],
  ['buzones'],
  ['buzones-grupo'],
  ['oportunidades'],
  ['etapas'],
  ['tipificaciones'],
  ['citas', 'campana'],
];
// Dentro de la lista blanca, lo que depende de filtros o busquedas de la pantalla no se guarda.
const EXCLUIDAS: (string | null)[][] = [['vistas-conteo', 'lista']];

const encaja = (k: QueryKey, patron: (string | null)[]) => patron.length <= k.length && patron.every((p, i) => p === null || p === k[i]);

export const esPersistible = (k: QueryKey) => LISTA_BLANCA.some((p) => encaja(k, p)) && !EXCLUIDAS.some((p) => encaja(k, p));

export const debeGuardarse = (q: Query) => defaultShouldDehydrateQuery(q) && esPersistible(q.queryKey);

// Las consultas guardadas viven 24 h en memoria aunque nadie las mire: si se borraran a los 30 min (gcTime normal),
// la siguiente copia ya no las tendria.
export function conservarPersistidas(qc: QueryClient) {
  for (const p of LISTA_BLANCA) if (!p.includes(null)) qc.setQueryDefaults(p, { gcTime: EDAD_MAXIMA });
}

// Misma separacion que cacheLocal.ts: empresa + persona. Sin copia en el puesto compartido ni sin sesion.
export function claveDeSesion(s: Session | null | undefined): string | null {
  if (!s || s.modo !== 'usuario') return null;
  return `consola.consultas.${s.workspace.id ?? s.workspace.nombre}.${s.email ?? ''}`;
}

/* ---------------- almacen (IndexedDB, o uno en memoria para las pruebas) ---------------- */

export type Almacen = {
  leer: (clave: string) => Promise<PersistedClient | undefined>;
  escribir: (clave: string, v: PersistedClient) => Promise<void>;
  borrar: (clave: string) => Promise<void>;
  borrarTodo: () => Promise<void>;
};

const BD = 'consola-cache';
const TABLA = 'consultas';

export function almacenIndexedDB(idb: IDBFactory | undefined = globalThis.indexedDB): Almacen | null {
  if (!idb) return null;
  let abierta: Promise<IDBDatabase> | null = null;
  const abrir = () =>
    (abierta ??= new Promise<IDBDatabase>((ok, mal) => {
      const r = idb.open(BD, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(TABLA);
      r.onsuccess = () => {
        const db = r.result;
        // Otra pestaña borra la base (cierre de sesion): se suelta para no bloquearla.
        db.onversionchange = () => (db.close(), (abierta = null));
        ok(db);
      };
      r.onerror = () => ((abierta = null), mal(r.error));
      r.onblocked = () => ((abierta = null), mal(new Error('IndexedDB bloqueado')));
    }));
  const tx = async <T>(modo: IDBTransactionMode, fn: (t: IDBObjectStore) => IDBRequest<T>) => {
    const db = await abrir();
    return new Promise<T>((ok, mal) => {
      const r = fn(db.transaction(TABLA, modo).objectStore(TABLA));
      r.onsuccess = () => ok(r.result);
      r.onerror = () => mal(r.error);
    });
  };
  return {
    leer: (k) => tx<PersistedClient | undefined>('readonly', (t) => t.get(k)),
    escribir: async (k, v) => void (await tx('readwrite', (t) => t.put(v, k))),
    borrar: async (k) => void (await tx('readwrite', (t) => t.delete(k))),
    borrarTodo: async () => void (await tx('readwrite', (t) => t.clear())),
  };
}

let almacenPorDefecto: Almacen | null | undefined;
const almacenActual = () => (almacenPorDefecto === undefined ? (almacenPorDefecto = almacenIndexedDB()) : almacenPorDefecto);

// Sube con cada borrado: una escritura que estaba esperando de antes del cierre de sesion ya no se hace.
let generacion = 0;

// Persister para React Query. Las escrituras se juntan (como mucho una por segundo): la cache cambia muchas veces por
// segundo mientras cargan las consultas. Cualquier fallo del almacen se ignora (la Consola funciona sin copia).
export function crearPersister(clave: string, almacen: Almacen, esperaMs = 1000): Persister {
  const gen = generacion;
  let pendiente: PersistedClient | null = null;
  let t: ReturnType<typeof setTimeout> | null = null;
  return {
    persistClient: (c) => {
      pendiente = c;
      t ??= setTimeout(() => {
        t = null;
        const v = pendiente;
        pendiente = null;
        if (v && gen === generacion) almacen.escribir(clave, v).catch(() => {});
      }, esperaMs);
    },
    // Mientras se lee la copia, React Query no pide nada: si el navegador no responde en 1,5 s, se sigue sin copia.
    restoreClient: () =>
      Promise.race([almacen.leer(clave).catch(() => undefined), new Promise<undefined>((ok) => setTimeout(() => ok(undefined), 1500))]),
    removeClient: () => almacen.borrar(clave).catch(() => {}),
  };
}

// Opciones para PersistQueryClientProvider, o null si esta sesion no lleva copia.
export function opcionesPersistencia(s: Session | null | undefined, almacen: Almacen | null = almacenActual()): Omit<PersistQueryClientOptions, 'queryClient'> | null {
  const clave = claveDeSesion(s);
  if (!clave || !almacen) return null;
  return {
    persister: crearPersister(clave, almacen),
    maxAge: EDAD_MAXIMA,
    buster: VERSION_CACHE,
    dehydrateOptions: { shouldDehydrateQuery: debeGuardarse },
  };
}

// Cierre de sesion: fuera todas las copias de este navegador (de cualquier empresa o persona).
export async function borrarCachePersistente(almacen: Almacen | null = almacenActual()) {
  generacion++;
  await almacen?.borrarTodo().catch(() => {});
}
